import React, { createContext, useContext, useId } from "react";
import cn from "classnames";

interface RadioGroupContextValue {
  name: string;
  value?: string;
  onValueChange?: (value: string) => void;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export interface RadioGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value?: string;
  onValueChange?: (value: string) => void;
  name?: string;
}

const RadioGroup = ({ value, onValueChange, name, className, children, ...props }: RadioGroupProps) => {
  const generatedName = useId();
  return (
    <RadioGroupContext.Provider value={{ name: name ?? generatedName, value, onValueChange }}>
      <div role="radiogroup" className={cn("grid gap-2", className)} {...props}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
};

export interface RadioGroupItemProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "name" | "checked" | "onChange"> {
  value: string;
}

const RadioGroupItem = React.forwardRef<HTMLInputElement, RadioGroupItemProps>(
  ({ value, className, ...props }, ref) => {
    const ctx = useContext(RadioGroupContext);
    if (!ctx) {
      throw new Error("RadioGroupItem must be used within a RadioGroup");
    }
    return (
      <input
        ref={ref}
        type="radio"
        name={ctx.name}
        value={value}
        checked={ctx.value === value}
        onChange={() => ctx.onValueChange?.(value)}
        className={cn("h-4 w-4 border-input text-primary focus-visible:ring-2 focus-visible:ring-primary", className)}
        {...props}
      />
    );
  }
);
RadioGroupItem.displayName = "RadioGroupItem";

export { RadioGroup, RadioGroupItem };
